import './Tooltip.scss'
import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { observeMovement, placeAnchored, Portal, pushEscape, rectOf, type Side } from '../layer'

export interface TooltipProps {
  content: ReactNode
  /** The trigger: one element, normally focusable. */
  children: ReactNode
  side?: Side
  /** Delay before showing on hover, ms. Keyboard focus shows immediately. */
  delay?: number
  /** Use the tooltip as the trigger's accessible name instead of its description. */
  isLabel?: boolean
  /**
   * Controlled: shown exactly while true, and hover and focus do nothing
   * (a confirmation such as "Copied"). Omit for the hover/focus tooltip.
   */
  isOpen?: boolean
}

/**
 * Hover/focus tooltip, portalled and anchored (layer/anchor). Escape hides
 * it. The wrapper is `display: contents`, so it adds no box to the layout.
 * Never put interactive content in a tooltip.
 */
export function Tooltip({
  content,
  children,
  side = 'top',
  delay = 400,
  isLabel = false,
  isOpen
}: TooltipProps) {
  const id = `tooltip-${useId()}`
  const [hoverOpen, setOpen] = useState(false)
  const isControlled = isOpen !== undefined
  const open = isOpen ?? hoverOpen
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const timer = useRef<number | undefined>(undefined)

  const trigger = () => wrapperRef.current?.firstElementChild ?? null

  const show = (immediate: boolean) => {
    if (isControlled) return
    window.clearTimeout(timer.current)
    if (immediate) setOpen(true)
    else
      timer.current = window.setTimeout(() => {
        setOpen(true)
      }, delay)
  }
  const hide = () => {
    window.clearTimeout(timer.current)
    setOpen(false)
  }

  useEffect(
    () => () => {
      window.clearTimeout(timer.current)
    },
    []
  )

  // Wire the trigger's ARIA: the label is permanent, the description only while shown.
  useEffect(() => {
    const el = trigger()
    if (!el) return
    if (isLabel) {
      el.setAttribute('aria-labelledby', id)
      return () => {
        el.removeAttribute('aria-labelledby')
      }
    }
    if (!open) return
    const previous = el.getAttribute('aria-describedby')
    el.setAttribute('aria-describedby', [previous, id].filter(Boolean).join(' '))
    return () => {
      if (previous) el.setAttribute('aria-describedby', previous)
      else el.removeAttribute('aria-describedby')
    }
  }, [open, isLabel, id])

  useEffect(() => {
    if (!open || isControlled) return
    return pushEscape(() => {
      setOpen(false)
      return true
    })
  }, [open, isControlled])

  useLayoutEffect(() => {
    const anchor = trigger()
    const surface = surfaceRef.current
    if (!open || !anchor || !surface) return
    return observeMovement(anchor, surface, () => {
      const p = placeAnchored(
        rectOf(anchor),
        { width: surface.offsetWidth, height: surface.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        { side }
      )
      surface.style.setProperty('--tooltip-top', `${p.top}px`)
      surface.style.setProperty('--tooltip-left', `${p.left}px`)
      surface.dataset.side = p.side
    })
  }, [open, side])

  return (
    <>
      <span
        ref={wrapperRef}
        className="tooltip-anchor"
        onMouseEnter={() => {
          show(false)
        }}
        onMouseLeave={hide}
        onFocus={(event) => {
          if (event.target.matches(':focus-visible')) show(true)
        }}
        onBlur={hide}
      >
        {children}
      </span>
      {isLabel && !open && (
        <span id={id} hidden>
          {content}
        </span>
      )}
      {open && (
        <Portal>
          <div ref={surfaceRef} id={id} role="tooltip" className="tooltip">
            {content}
          </div>
        </Portal>
      )}
    </>
  )
}
